import React from "react";
import styled from "styled-components";

const formatTipoAtividade = (tipo) => {
  const tipos = {
    ESTAGIO_OBRIGATORIO: "Estágio Obrigatório",
    ESTAGIO_SUPERVISIONADO: "Estágio Supervisionado",
    INTERNATO_MEDICO: "Internato Médico",
    AULA_PRATICA: "Aula Prática",
  };
  return tipos[tipo] || tipo || "-";
};

const formatDate = (dateValue) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("pt-BR");
};

export const getPeriodoInicio = (periodos = []) => {
  if (!periodos.length) return "";
  const sorted = [...periodos].sort(
    (a, b) => new Date(a.dataInicio).getTime() - new Date(b.dataInicio).getTime()
  );
  return formatDate(sorted[0].dataInicio);
};

export const getPeriodoTermino = (periodos = []) => {
  if (!periodos.length) return "";
  const sorted = [...periodos].sort(
    (a, b) => new Date(b.dataFim).getTime() - new Date(a.dataFim).getTime()
  );
  return formatDate(sorted[0].dataFim);
};

export const getDiasSemanaSolicitacao = (diasSemana = []) =>
  diasSemana
    .map((d) => (typeof d === "string" ? d : d?.diaSemana))
    .filter(Boolean);

const formatPeriodo = (periodos = []) => {
  if (!periodos || periodos.length === 0) return "-";
  const normalized = [...periodos].sort(
    (a, b) => new Date(a.dataInicio).getTime() - new Date(b.dataInicio).getTime()
  );
  return normalized
    .map((periodo) => {
      const inicio = formatDate(periodo.dataInicio);
      const fim = formatDate(periodo.dataFim);
      if (!inicio && !fim) return "";
      if (!inicio) return fim;
      if (!fim) return inicio;
      return `${inicio} - ${fim}`;
    })
    .filter(Boolean)
    .join(" | ");
};

const getInitials = (nome = "") => {
  const parts = nome.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return nome.slice(0, 2).toUpperCase();
};

const CardOuter = styled.div`
  background: #fff;
  border-top: 1px solid #e0e0e0;
  border-right: 1px solid #f3f3f3;
  border-bottom: 1px solid #f3f3f3;
  border-left: none;
  overflow: hidden;
  max-width: ${({ $wide }) => ($wide ? "100%" : "700px")};
  margin: ${({ $wide }) => ($wide ? "0" : "20px 20px 0 0px")};
`;

const CardTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 22px;
  background: #e8f5f3;
  border-bottom: 1px solid #d0d0d0;
`;

const UnitInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const UnitLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const UnitName = styled.div`
  min-width: 0;
  overflow: hidden;
  font-size: 16px;
  color: #555555;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Avatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 20%;
  background: #6ab8a8;
  color: white;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const UnitLabel = styled.div`
  font-size: 16px;
  color: #6ab8a8;
  font-weight: 600;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: minmax(112px, 42%) minmax(0, 1fr);
  gap: 12px;
  align-items: center;
  padding: 10px 22px;
  border-bottom: 1px solid #efefef;
  min-height: 42px;

  &:last-child {
    border-bottom: none;
  }
`;

const FieldLabel = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #555555;
  line-height: 1.3;
`;

const FieldValue = styled.div`
  min-width: 0;
  font-size: 16px;
  color: #555;
  line-height: 1.4;
  overflow-wrap: anywhere;
`;

const VagaResumoCard = ({ solicitacao, wide = false }) => {
  const vaga = solicitacao?.vaga || {};
  const unidadeNome = vaga.estabelecimentoSaude?.nome || "-";
  const curso = vaga.areaCurso?.nome || solicitacao?.curso?.curso || "-";
  const setor = vaga.unidadeAssistencial?.nome || "-";
  const periodo = formatPeriodo(solicitacao?.periodos);
  const turno = vaga.turno || "-";
  const qtdAlunos =
    solicitacao?.quantidadeAlunosConcedidos ??
    solicitacao?.quantidadeAlunos ??
    null;
  const horasPorAluno = solicitacao?.horasTotaisPorAluno ?? null;
  const totalHoras =
    qtdAlunos !== null && horasPorAluno !== null
      ? Number(qtdAlunos) * Number(horasPorAluno)
      : null;

  return (
    <CardOuter $wide={wide}>
      <CardTopRow>
        <UnitInfo>
          <UnitLeft>
            <Avatar>{getInitials(unidadeNome)}</Avatar>
            <UnitLabel>Unidade de Saúde:</UnitLabel>
          </UnitLeft>
          <UnitName>{unidadeNome}</UnitName>
        </UnitInfo>
      </CardTopRow>

      <FieldRow>
        <FieldLabel>Curso:</FieldLabel>
        <FieldValue>{curso}</FieldValue>
      </FieldRow>

      <FieldRow>
        <FieldLabel>Setor:</FieldLabel>
        <FieldValue>{setor}</FieldValue>
      </FieldRow>

      <FieldRow>
        <FieldLabel>Período:</FieldLabel>
        <FieldValue>{periodo}</FieldValue>
      </FieldRow>

      <FieldRow>
        <FieldLabel>Turno:</FieldLabel>
        <FieldValue>{turno}</FieldValue>
      </FieldRow>

      <FieldRow>
        <FieldLabel>Tipo de Atividade:</FieldLabel>
        <FieldValue>{formatTipoAtividade(vaga.tipoAtividade)}</FieldValue>
      </FieldRow>

      <FieldRow>
        <FieldLabel>Nº de Alunos / unidade:</FieldLabel>
        <FieldValue>
          {qtdAlunos !== null ? String(qtdAlunos).padStart(2, "0") : "-"}
        </FieldValue>
      </FieldRow>

      <FieldRow>
        <FieldLabel>
          Nº de horas / aluno
          <br />
          por unidade:
        </FieldLabel>
        <FieldValue>
          {horasPorAluno !== null ? `${horasPorAluno}h` : "-"}
        </FieldValue>
      </FieldRow>

      <FieldRow>
        <FieldLabel>Nº Total de horas:</FieldLabel>
        <FieldValue>{totalHoras !== null ? `${totalHoras}h` : "-"}</FieldValue>
      </FieldRow>
    </CardOuter>
  );
};

export default VagaResumoCard;
